import { getIn, setIn } from "formik";
import { queryKeys } from "./queryKeys";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { globalAPI } from "./global";

let refCII: number = 0;
let reqCII: number = 0;

/**
 * Digit budgets for the numeric boxes on the FuelEU forms, as whole digits
 * before the decimal point.
 *
 * Each is the widest value the field can legitimately hold, so the cap only ever
 * stops a typo: 999,999 mt of bunker, 99,999,999 kWh off a shore connection, and
 * an E value of 999 gCO2eq/MJ against a scale whose fossil baseline is 91.
 */
export const MAX_QUANTITY_DIGITS = 6;
export const MAX_OPS_DIGITS = 8;

/**
 * Decimal places on a quantity. Three is a kilogramme in a tonne, which is finer
 * than any bunker note reports and far finer than the compliance balance can
 * resolve — past it the digits are noise the box should not invite.
 */
export const MAX_QUANTITY_DECIMALS = 3;

/**
 * A quantity entered as a daily rate rather than a period total. Three digits,
 * because it is one ship's burn for one day — a figure that reaches 999 mt/day
 * only in the largest classes, where the period total behind it is six figures.
 */
export const MAX_DAILY_QUANTITY_DIGITS = 3;
export const MAX_E_VALUE_DIGITS = 3;

/**
 * Decimals on the two fields a supplier or a meter quotes rather than the form
 * derives. An OPS connection is invoiced in whole kWh and an Annex V E value is
 * published to a decimal or two, so past two places neither number is being read
 * off anything — it is being invented.
 */
export const MAX_OPS_DECIMALS = 2;
export const MAX_E_VALUE_DECIMALS = 2;

/**
 * LCV is the exception: in MJ/g it is always below 1 — 0.0405 for HFO — so a
 * whole-digit cap would constrain nothing and the precision is what needs
 * bounding. Three whole digits are still allowed so that an LCV entered in MJ/kg
 * by mistake (40.5) can be typed and then flagged as implausible by the echo
 * under the field, rather than silently losing keystrokes as it is entered.
 */
export const MAX_LCV_DIGITS = 3;
export const MAX_LCV_DECIMALS = 5;

/** Whole digits and decimals allowed in a blend share. */
export const MAX_PERCENT_DIGITS = 3;
export const MAX_PERCENT_DECIMALS = 3;

/**
 * Constrain a non-negative decimal as the user types: digits only, a single
 * decimal point, at most `wholeDigits` before it and `decimals` after it when
 * given (unbounded when not).
 *
 * Stripping everything outside [0-9.] is also what blocks negatives — a leading
 * "-" never survives, so there is no state in which the field holds one. Doing it
 * here rather than in a Yup rule means the invalid character never reaches the
 * form, so the totals derived from these fields can never be computed from one.
 */
export const sanitizeDecimal = (
  raw: any,
  wholeDigits: number,
  decimals?: number
): string => {
  const text = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = text.indexOf(".");
  if (dot === -1) {
    return text.slice(0, wholeDigits);
  }
  // A field allowed no decimals is a whole-number field, so the point itself is
  // dropped along with what follows it. Keeping it would leave a trailing "12."
  // in form state, which no integer parser takes — the API's included.
  if (decimals === 0) {
    return text.slice(0, dot).slice(0, wholeDigits);
  }
  const whole = text.slice(0, dot).slice(0, wholeDigits);
  const fraction = text.slice(dot + 1).replace(/\./g, "");
  return whole + "." + (decimals === undefined
    ? fraction
    : fraction.slice(0, decimals));
};

export const sanitizeQuantity = (raw: any): string =>
  sanitizeDecimal(raw, MAX_QUANTITY_DIGITS, MAX_QUANTITY_DECIMALS);

/** What a numeric box will accept. */
export type NumericLimits = {
  /** Digits allowed before the decimal point. */
  wholeDigits: number;
  /** Decimal places allowed. Unbounded when omitted. */
  decimals?: number;
  /** Largest value the field may hold, applied after the digit caps. */
  max?: number;
};

/**
 * Apply `limits` to what was typed, and say what had to be changed.
 *
 * These fields correct the entry rather than reject it, so without a word from
 * the field the correction is silent: a digit that does not appear reads as a
 * dropped keystroke, and a value that snaps back to 100 reads as a bug. The
 * notice is what turns the correction into an explanation, so it names the rule
 * that acted rather than restating the rule for the field in general — nothing
 * is said while the entry is inside its limits.
 */
export const applyLimits = (
  raw: any,
  limits: NumericLimits
): { text: string; notice: string | null } => {
  const typed = String(raw ?? "");
  const text = sanitizeDecimal(typed, limits.wholeDigits, limits.decimals);

  if (limits.max !== undefined && text !== "") {
    const value = Number(text);
    if (Number.isFinite(value) && value > limits.max) {
      return {
        text: String(limits.max),
        notice: `Cannot be more than ${limits.max}.`,
      };
    }
  }

  if (text === typed) return { text, notice: null };

  // Ordered by what the reader most needs told. A stripped "-" is a different
  // mistake from a stripped letter, and both are more surprising than a digit
  // cap the field can at least be seen to be holding.
  if (typed.includes("-")) {
    return { text, notice: "Cannot be negative." };
  }
  if (/[^\d.]/.test(typed)) {
    return { text, notice: "Numbers only." };
  }

  const dot = typed.indexOf(".");
  const whole = dot === -1 ? typed : typed.slice(0, dot);
  if (whole.length > limits.wholeDigits) {
    const digits = `${limits.wholeDigits} digit${
      limits.wholeDigits === 1 ? "" : "s"
    }`;
    return {
      text,
      // A whole-number field has no decimal point to be "before", so the cap is
      // named plainly there.
      notice:
        limits.decimals === 0
          ? `Up to ${digits}.`
          : `Up to ${digits} before the decimal point.`,
    };
  }
  if (limits.decimals === 0 && dot !== -1) {
    return { text, notice: "Whole numbers only." };
  }
  if (
    limits.decimals !== undefined &&
    dot !== -1 &&
    typed.slice(dot + 1).replace(/\./g, "").length > limits.decimals
  ) {
    return {
      text,
      notice: `Up to ${limits.decimals} decimal place${
        limits.decimals === 1 ? "" : "s"
      }.`,
    };
  }
  return { text, notice: "Only one decimal point." };
};

/**
 * True when the box is showing a plain zero — "0", "0.0", 0. An empty box is
 * not a zero: a field the user has already cleared must not count as one.
 */
export const isZeroValue = (raw: any): boolean => {
  const text = String(raw ?? "").trim();
  return text !== "" && Number(text) === 0;
};

/**
 * Focus half of the clear-the-default-zero pair: wipes the box when it is
 * showing 0, so the user can type straight over it instead of selecting and
 * deleting the 0 first.
 *
 * Only wire this onto fields whose initial value really is 0. On a field that
 * starts null it would do nothing on focus but still let `zeroIfEmpty` write a
 * 0 on the way out, which would satisfy a Yup `required` rule the user never
 * actually answered.
 */
export const clearZeroOnFocus = (form: any, name: string) => (e: any) => {
  if (isZeroValue(e.target.value)) form.setFieldValue(name, "");
};

/**
 * Blur half: puts the 0 back when the box was left empty.
 *
 * This is what keeps clearing safe. These values feed the CII/EUA arithmetic
 * and the Yup rules directly, so a field the user clicked into and left without
 * typing must not be allowed to hold "" where it held a number.
 */
export const zeroIfEmpty = (form: any, name: string, value: any) => {
  if (String(value ?? "").trim() === "") form.setFieldValue(name, 0);
};

/**
 * The blur handler for a plain `<Field>`. Calls `handleBlur` itself because
 * passing `onBlur` to a Field replaces Formik's own, which is what marks the
 * field touched.
 */
export const restoreZeroOnBlur = (form: any, name: string) => (e: any) => {
  zeroIfEmpty(form, name, e.target.value);
  form.handleBlur(e);
};

/**
 * True when `optionId` is already chosen in one of `siblingFields` other than
 * `currentField` — used by the fuel dropdowns to grey out a fuel that is already
 * in use, so the same fuel cannot be picked twice in one group.
 *
 * Sea-passage and port-stay fuels are separate groups: a vessel may legitimately
 * burn the same fuel at sea and at berth, and the API folds the two legs together
 * by fuel id, so they must not block each other.
 */
export const isFuelSelectedElsewhere = (
  values: any,
  currentField: string,
  siblingFields: string[],
  optionId: any
): boolean =>
  siblingFields.some((name) => {
    if (name === currentField) return false;
    const selected = getIn(values, name);
    if (selected === null || selected === undefined || selected === "") return false;
    return String(selected) === String(optionId);
  });

/**
 * Choose a fuel in one of the voyage dropdowns, mirroring the id into the
 * `total*` twin the submit payload is built from.
 *
 * Deliberately one `setValues` and not two `setFieldValue` calls. Formik
 * revalidates a `setFieldValue` against `state.values` as it stood at the start
 * of the event — `validateFormWithHighPriority(setIn(state.values, field, value))`
 * in formik 2.4.5 — so a second call in the same handler validates a form that
 * is still missing the first call's change. Being second, its result lands last
 * and wins, which is what left "Field is required!" under a dropdown the user
 * had just answered. `setValues` validates the object it is handed, so both
 * changes are present in the one run.
 */
export const selectFuel = (
  form: any,
  name: string,
  mirrorName: string,
  option: any
) => {
  const id = option == null ? "" : option.id;
  form.setValues(setIn(setIn(form.values, name, id), mirrorName, id));
};

/** Fuel dropdown groups, one entry per mutually-exclusive set. */
export const CII_FUEL_FIELDS = [
  "type_of_fuel1",
  "type_of_fuel2",
  "type_of_fuel3",
];
export const SEA_FUEL_FIELDS = [
  "seaPassage.fuel_type1",
  "seaPassage.fuel_type2",
  "seaPassage.fuel_type3",
];
export const PORT_FUEL_FIELDS = [
  "portStay.fuel_type1",
  "portStay.fuel_type2",
  "portStay.fuel_type3",
];

export const CO2Emission = (values: any) => {
  let cf1 = fuelCF(values.type_of_fuel1) ?? 0;
  let cf2 = fuelCF(values.type_of_fuel2) ?? 0;
  let cf3 = fuelCF(values.type_of_fuel3) ?? 0;
  return (
    values.fuel_type1_value * cf1 +
    values.fuel_type2_value * cf2 +
    values.fuel_type3_value * cf3
  );
};

export const successToast = (message: string) => {
  toast.dismiss();
  toast.success(message, {
    position: "top-right",
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: 0,
    theme: "colored",
  });
};

export const loadingToast = (message: string) => {
  toast.dismiss();
  toast.info(message, {
    position: "top-right",
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: 0,
    theme: "colored",
    isLoading: true,
  });
};

/**
 * Words in a field name that are read as acronyms rather than spelled out, so
 * "dwt_of_vessel" comes back as "DWT of vessel" and not "Dwt of vessel".
 */
const FIELD_ACRONYMS = new Set(["dwt", "imo", "gt", "cii", "eua", "ops", "lcv"]);

/** A serializer field name as the form labels it. */
const fieldLabel = (name: string): string =>
  name
    .split("_")
    .map((word) => (FIELD_ACRONYMS.has(word) ? word.toUpperCase() : word))
    .join(" ")
    .replace(/^./, (first) => first.toUpperCase());

/**
 * Flatten a DRF error body into readable lines.
 *
 * The body is either {"detail": "..."} for a whole-request refusal or
 * {"field": ["...", ...]} for a per-field one, and a nested serializer nests the
 * same shape again. Only a field-keyed message is prefixed with its field —
 * "detail" and "non_field_errors" are not field names, so naming them would read
 * as one.
 */
const errorMessages = (value: any, field?: string): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => errorMessages(item, field));
  }
  if (typeof value === "object") {
    return Object.entries(value).flatMap(([key, nested]) =>
      errorMessages(
        nested,
        key === "detail" || key === "non_field_errors" ? undefined : key
      )
    );
  }
  const text = String(value).trim();
  if (text === "") return [];
  return [field ? `${fieldLabel(field)}: ${text}` : text];
};

/**
 * What actually went wrong with a request, for showing to the user.
 *
 * An axios error stringifies to "AxiosError: Request failed with status code
 * 400", which says only that the API refused the submission — never which field
 * it refused or why, although the response body says exactly that. This reads
 * the body instead, and falls back to the axios text only when there is no body
 * to read: a request that never reached the API, or a 500 whose body is Django's
 * HTML error page rather than a message.
 */
export const apiErrorMessage = (err: any): string => {
  const data = err?.response?.data;
  const messages =
    typeof data === "string"
      ? /<\/?[a-z]/i.test(data) || data.trim() === ""
        ? []
        : [data.trim()]
      : errorMessages(data);
  if (messages.length > 0) return Array.from(new Set(messages)).join(" ");
  return err?.message ? String(err.message) : "Something went wrong.";
};

export const errorToast = (message: string) => {
  toast.dismiss();
  toast.error(message, {
    position: "top-right",
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: true,
    progress: 0,
    theme: "colored",
  });
};

export const VesselTypes: any[] = [
  { id: "1", label: "Bulk Carrier" },
  { id: "2", label: "Gas Carrier" },
  { id: "3", label: "Tanker" },
  { id: "4", label: "Container Ship" },
  { id: "5", label: "General Cargo Ship" },
  { id: "6", label: "Refrigerated Cargo Carrier" },
  { id: "7", label: "Combination Carrier" },
  { id: "8", label: "LNG Carrier" },
  { id: "9", label: "Ro-Ro Cargo Ship(VC)" },
  { id: "10", label: "Ro-Ro Cargo Ship" },
  { id: "11", label: "Ro-Ro Passenger Ship" },
  { id: "12", label: "Cruise Passenger Ship" },
];

export const Year: any[] = [
  { id: "1", label: "2023" },
  { id: "2", label: "2024" },
  { id: "3", label: "2025" },
  { id: "4", label: "2026" },
  { id: "5", label: "2027" },
  { id: "6", label: "2028" },
  { id: "7", label: "2029" },
  { id: "8", label: "2030" },
  { id: "9", label: "2031" },
  { id: "10", label: "2032" },
  { id: "11", label: "2033" },
  { id: "12", label: "2034" },
  { id: "13", label: "2035" },
];

/**
 * Years offered by both calculators' Year dropdown: 2026-2035.
 *
 * Note this is wider than the range the CII maths covers. calculateReqCII (and
 * the API's get_z_factor_for_year) define a reduction factor only up to 2030;
 * from 2031 the factor falls through to 0, so Required CII comes out equal to
 * Reference CII. Extending the dropdown without the factors was a deliberate
 * call — add the 2031+ factors in both places to make those years meaningful.
 */
export const CalculatorYears: any[] = Year.filter(
  (item: any) => Number(item.label) >= 2026 && Number(item.label) <= 2035
);

export const euaMultiplyingFactor = (year: string) => {
  if (year === "2024") {
    return 0.4;
  } else if (year === "2025") {
    return 0.7;
  } else {
    return 1;
  }
};

export const fuelCF = (fuel: number) => {
  if (fuel === 1) {
    return 3.206;
  } else if (fuel === 2) {
    return 3.151;
  } else if (fuel === 3) {
    return 3.114;
  } else if (fuel === 4) {
    return 3;
  } else if (fuel === 5) {
    return 3.03;
  } else if (fuel === 6) {
    return 2.75;
  } else if (fuel === 7) {
    return 1.375;
  } else if (fuel === 8) {
    return 1.913;
  }
};

export const calculateCapacity = (values: any) => {
  if (
    values.type_of_vessel === "Bulk Carrier" &&
    values.dwt_of_vessel >= 279000
  ) {
    return 279000;
  } else if (
    values.type_of_vessel === "Bulk Carrier" &&
    values.dwt_of_vessel < 279000
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Gas Carrier" &&
    values.dwt_of_vessel >= 65000
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Gas Carrier" &&
    values.dwt_of_vessel < 65000
  ) {
    return values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Tanker") {
    return values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Container Ship") {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "General Cargo Ship" &&
    values.dwt_of_vessel >= 20000
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "General Cargo Ship" &&
    values.dwt_of_vessel < 20000
  ) {
    return values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Refrigerated Cargo Carrier") {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Combination Carrier" &&
    values.dwt_of_vessel >= 40853
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    values.dwt_of_vessel >= 100000
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    100000 > values.dwt_of_vessel &&
    values.dwt_of_vessel >= 65000
  ) {
    return values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    values.dwt_of_vessel < 65000
  ) {
    return 65000;
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship(VC)") {
    return values.gross_tonnage;
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship") {
    return values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Ro-Ro Passenger Ship") {
    return values.gross_tonnage;
  } else if (values.type_of_vessel === "Cruise Passenger Ship") {
    return values.gross_tonnage;
  }
};

export const calculateAttCII = (values: any) => {
  let cf1 = fuelCF(values.type_of_fuel1) ?? 0;
  let cf2 = fuelCF(values.type_of_fuel2) ?? 0;
  let cf3 = fuelCF(values.type_of_fuel3) ?? 0;
  let capacity: number = 0;
  if (
    values.type_of_vessel === "Bulk Carrier" &&
    values.dwt_of_vessel >= 279000
  ) {
    capacity = 279000;
  } else if (
    values.type_of_vessel === "Bulk Carrier" &&
    values.dwt_of_vessel < 279000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Gas Carrier" &&
    values.dwt_of_vessel >= 65000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Gas Carrier" &&
    values.dwt_of_vessel < 65000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Tanker") {
    capacity = values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Container Ship") {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "General Cargo Ship" &&
    values.dwt_of_vessel >= 20000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "General Cargo Ship" &&
    values.dwt_of_vessel < 20000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Refrigerated Cargo Carrier") {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "Combination Carrier" &&
    values.dwt_of_vessel >= 40853
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    values.dwt_of_vessel >= 100000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    100000 > values.dwt_of_vessel &&
    values.dwt_of_vessel >= 65000
  ) {
    capacity = values.dwt_of_vessel;
  } else if (
    values.type_of_vessel === "LNG Carrier" &&
    values.dwt_of_vessel < 65000
  ) {
    capacity = 65000;
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship(VC)") {
    capacity = values.gross_tonnage;
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship") {
    capacity = values.dwt_of_vessel;
  } else if (values.type_of_vessel === "Ro-Ro Passenger Ship") {
    capacity = values.gross_tonnage;
  } else if (values.type_of_vessel === "Cruise Passenger Ship") {
    capacity = values.gross_tonnage;
  }
  return (
    (values.fuel_type1_value * cf1 +
      values.fuel_type2_value * cf2 +
      values.fuel_type3_value * cf3) /
    (capacity * values.distance_travelled)
  );
};

export const calculateRefCII = (
  type: string | null,
  dwt: number,
  gt: number
) => {
  let a: number = 0;
  let c: number = 0;
  let capacity: number = 0;
  if (type === "Bulk Carrier" && dwt >= 279000) {
    a = 4745;
    c = 0.622;
    capacity = 279000;
  } else if (type === "Bulk Carrier" && dwt < 279000) {
    a = 4745;
    c = 0.622;
    capacity = dwt;
  } else if (type === "Gas Carrier" && dwt >= 65000) {
    a = 144050000000;
    c = 2.071;
    capacity = dwt;
  } else if (type === "Gas Carrier" && dwt < 65000) {
    a = 8104;
    c = 0.639;
    capacity = dwt;
  } else if (type === "Tanker") {
    a = 5247;
    c = 0.61;
    capacity = dwt;
  } else if (type === "Container Ship") {
    a = 1984;
    c = 0.489;
    capacity = dwt;
  } else if (type === "General Cargo Ship" && dwt >= 20000) {
    a = 31948;
    c = 0.792;
    capacity = dwt;
  } else if (type === "General Cargo Ship" && dwt < 20000) {
    a = 588;
    c = 0.3885;
    capacity = dwt;
  } else if (type === "Refrigerated Cargo Carrier") {
    a = 4600;
    c = 0.557;
    capacity = dwt;
  } else if (type === "Combination Carrier" && dwt >= 40853) {
    a = 40853;
    c = 0.812;
    capacity = dwt;
  } else if (type === "LNG Carrier" && dwt >= 100000) {
    a = 9.827;
    c = 0;
    capacity = dwt;
  } else if (type === "LNG Carrier" && 100000 > dwt && dwt >= 65000) {
    a = 144790000000000;
    c = 2.673;
    capacity = dwt;
  } else if (type === "LNG Carrier" && dwt < 65000) {
    a = 144790000000000;
    c = 2.673;
    capacity = 65000;
  } else if (type === "Ro-Ro Cargo Ship(VC)") {
    a = 5739;
    c = 0.631;
    capacity = gt;
  } else if (type === "Ro-Ro Cargo Ship") {
    a = 10952;
    c = 0.637;
    capacity = dwt;
  } else if (type === "Ro-Ro Passenger Ship") {
    a = 7540;
    c = 0.587;
    capacity = gt;
  } else if (type === "Cruise Passenger Ship") {
    a = 930;
    c = 0.383;
    capacity = gt;
  }
  return (refCII = a * Math.pow(capacity, -c));
};

/**
 * CII reduction factor Z, in percent, keyed by the year as the form holds it.
 *
 * The exact values, not rounded: 2027-2030 are fractional and writing them as
 * 14/17/20/23 made this preview compute a Required CII the API would never
 * agree with. 2023-2025 are the factors adopted in MARPOL Annex VI reg. 28;
 * from 2026 the factor steps by 2.625 points a year.
 *
 * The API holds the same ladder in its `ZFactorMaster` table
 * (`database/ecosail_tbl_additional_factor_for_calculation.sql`), which is the
 * authority — this copy exists only so the form can show a Required CII before
 * anything is saved. Revising the factors means changing both, and rebuilding
 * this bundle with `yarn build:api`; a year missing here yields 0, i.e.
 * Required CII equal to Reference CII, which is what the old ladder's fall-through
 * did for 2031+.
 */
const Z_FACTORS: Record<string, number> = {
  "2023": 5,
  "2024": 7,
  "2025": 9,
  "2026": 11,
  "2027": 13.625,
  "2028": 16.25,
  "2029": 18.875,
  "2030": 21.5,
};

export const calculateReqCII = (values: any) => {
  refCII = calculateRefCII(
    values.type_of_vessel,
    values.dwt_of_vessel,
    values.gross_tonnage
  );
  const z: number = Z_FACTORS[values.year] ?? 0;
  return (reqCII = refCII * ((100 - z) / 100));
};

export function removeBodyCss() {
  document.body.classList.add("no_padding");
}

export function tog_backdrop(setModalState: any) {
  setModalState(false);
  removeBodyCss();
}

export const CIICategory = (values: any, ratio: any) => {
  if (values.type_of_vessel === "Bulk Carrier") {
    if (ratio >= 0 && ratio <= 0.86) {
      return "A";
    } else if (ratio > 0.86 && ratio <= 0.94) {
      return "B";
    } else if (ratio > 0.94 && ratio <= 1.06) {
      return "C";
    } else if (ratio > 1.06 && ratio <= 1.18) {
      return "D";
    } else if (ratio > 1.18) {
      return "E";
    }
  } else if (values.type_of_vessel === "Gas Carrier") {
    if (values.dwt_of_vessel >= 65000) {
      if (ratio >= 0 && ratio <= 0.81) {
        return "A";
      } else if (ratio > 0.81 && ratio <= 0.91) {
        return "B";
      } else if (ratio > 0.91 && ratio <= 1.12) {
        return "C";
      } else if (ratio > 1.12 && ratio <= 1.44) {
        return "D";
      } else if (ratio > 1.44) {
        return "E";
      }
    } else if (values.dwt_of_vessel < 65000) {
      if (ratio >= 0 && ratio <= 0.85) {
        return "A";
      } else if (ratio > 0.85 && ratio <= 0.95) {
        return "B";
      } else if (ratio > 0.95 && ratio <= 1.06) {
        return "C";
      } else if (ratio > 1.06 && ratio <= 1.25) {
        return "D";
      } else if (ratio > 1.25) {
        return "E";
      }
    }
  } else if (values.type_of_vessel === "Tanker") {
    if (ratio >= 0 && ratio <= 0.82) {
      return "A";
    } else if (ratio > 0.82 && ratio <= 0.93) {
      return "B";
    } else if (ratio > 0.93 && ratio <= 1.08) {
      return "C";
    } else if (ratio > 1.08 && ratio <= 1.28) {
      return "D";
    } else if (ratio > 1.28) {
      return "E";
    }
  } else if (values.type_of_vessel === "Container Ship") {
    if (ratio >= 0 && ratio <= 0.83) {
      return "A";
    } else if (ratio > 0.83 && ratio <= 0.94) {
      return "B";
    } else if (ratio > 0.94 && ratio <= 1.07) {
      return "C";
    } else if (ratio > 1.07 && ratio <= 1.19) {
      return "D";
    } else if (ratio > 1.19) {
      return "E";
    }
  } else if (values.type_of_vessel === "General Cargo Ship") {
    if (ratio >= 0 && ratio <= 0.83) {
      return "A";
    } else if (ratio > 0.83 && ratio <= 0.94) {
      return "B";
    } else if (ratio > 0.94 && ratio <= 1.06) {
      return "C";
    } else if (ratio > 1.06 && ratio <= 1.19) {
      return "D";
    } else if (ratio > 1.19) {
      return "E";
    }
  } else if (values.type_of_vessel === "Refrigerated Cargo Carrier") {
    if (ratio >= 0 && ratio <= 0.78) {
      return "A";
    } else if (ratio > 0.78 && ratio <= 0.91) {
      return "B";
    } else if (ratio > 0.91 && ratio <= 1.07) {
      return "C";
    } else if (ratio > 1.07 && ratio <= 1.2) {
      return "D";
    } else if (ratio > 1.2) {
      return "E";
    }
  } else if (values.type_of_vessel === "Combination Carrier") {
    if (ratio >= 0 && ratio <= 0.87) {
      return "A";
    } else if (ratio > 0.87 && ratio <= 0.96) {
      return "B";
    } else if (ratio > 0.96 && ratio <= 1.06) {
      return "C";
    } else if (ratio > 1.06 && ratio <= 1.14) {
      return "D";
    } else if (ratio > 1.14) {
      return "E";
    }
  } else if (values.type_of_vessel === "LNG Carrier") {
    if (values.dwt_of_vessel >= 100000) {
      if (ratio >= 0 && ratio <= 0.89) {
        return "A";
      } else if (ratio > 0.89 && ratio <= 0.98) {
        return "B";
      } else if (ratio > 0.98 && ratio <= 1.06) {
        return "C";
      } else if (ratio > 1.06 && ratio <= 1.13) {
        return "D";
      } else if (ratio > 1.13) {
        return "E";
      }
    } else if (values.dwt_of_vessel < 100000) {
      if (ratio >= 0 && ratio <= 0.78) {
        return "A";
      } else if (ratio > 0.78 && ratio <= 0.92) {
        return "B";
      } else if (ratio > 0.92 && ratio <= 1.1) {
        return "C";
      } else if (ratio > 1.1 && ratio <= 1.37) {
        return "D";
      } else if (ratio > 1.37) {
        return "E";
      }
    }
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship(VC)") {
    if (ratio >= 0 && ratio <= 0.86) {
      return "A";
    } else if (ratio > 0.86 && ratio <= 0.94) {
      return "B";
    } else if (ratio > 0.94 && ratio <= 1.06) {
      return "C";
    } else if (ratio > 1.06 && ratio <= 1.16) {
      return "D";
    } else if (ratio > 1.16) {
      return "E";
    }
  } else if (values.type_of_vessel === "Ro-Ro Cargo Ship") {
    if (ratio >= 0 && ratio <= 0.66) {
      return "A";
    } else if (ratio > 0.66 && ratio <= 0.9) {
      return "B";
    } else if (ratio > 0.9 && ratio <= 1.11) {
      return "C";
    } else if (ratio > 1.11 && ratio <= 1.37) {
      return "D";
    } else if (ratio > 1.37) {
      return "E";
    }
  } else if (values.type_of_vessel === "Ro-Ro Passenger Ship") {
    if (ratio >= 0 && ratio <= 0.72) {
      return "A";
    } else if (ratio > 0.72 && ratio <= 0.9) {
      return "B";
    } else if (ratio > 0.9 && ratio <= 1.12) {
      return "C";
    } else if (ratio > 1.12 && ratio <= 1.41) {
      return "D";
    } else if (ratio > 1.41) {
      return "E";
    }
  } else if (values.type_of_vessel === "Cruise Passenger Ship") {
    if (ratio >= 0 && ratio <= 0.87) {
      return "A";
    } else if (ratio > 0.87 && ratio <= 0.95) {
      return "B";
    } else if (ratio > 0.95 && ratio <= 1.06) {
      return "C";
    } else if (ratio > 1.06 && ratio <= 1.16) {
      return "D";
    } else if (ratio > 1.16) {
      return "E";
    }
  }
};

export const loadFuelTypes = async () => {
  try {
    const res = await globalAPI.get(queryKeys.FuelTypesMaster.url());
    return res.data;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const loadVesselTypes = async () => {
  try {
    const response = await globalAPI.get(queryKeys.VesselTypesMaster.url());
    return response.data;
  } catch (error) {
    console.log(error);
    return [];
  }
};
